import { IconCheck, IconMinus } from '@doppio/icons';
import { Text, useDripsyTheme, View } from 'dripsy';
import * as React from 'react';
import { useFocus, useHover } from 'react-native-web-hooks';
import { TEST_ID } from './types';
const Checkbox = React.forwardRef(({ accessibilityHint, accessibilityLabel, children, id, isIndeterminate = false, isChecked = false, isDisabled = false, name, onChange, size, }, forwardedRef) => {
    const theme = useDripsyTheme().theme;
    const { checkbox } = theme.forms;
    const { $$hover, $$checked, $$focus, $$disabled } = checkbox;
    const ref = React.useRef(null);
    React.useImperativeHandle(forwardedRef, () => ref.current);
    const refLabel = React.useRef(null);
    const [isPressed, setIsPressed] = React.useState(false);
    const isFocusVisible = useFocus(ref);
    const isHovered = useHover(refLabel);
    const isSizeBig = size === 'big';
    const shouldRenderHover = !isDisabled && isHovered;
    const shouldRenderFocus = !isDisabled && (isPressed || isFocusVisible);
    const shouldRenderChecked = isChecked || isIndeterminate;
    const shouldRenderIconCheck = isChecked && !isIndeterminate;
    const shouldRenderIconMinus = isIndeterminate;
    const shouldRenderIcon = shouldRenderIconCheck || shouldRenderIconMinus;
    const hoverColor = 'color' in checkbox.$$hover ? checkbox.$$hover['color'] : checkbox.color;
    const checkedColor = 'color' in checkbox.$$checked ? checkbox.$$checked['color'] : checkbox.color;
    const disabledColor = 'color' in checkbox.$$disabled ? checkbox.$$disabled['color'] : checkbox.color;
    let iconColor = checkbox.color;
    if (isChecked || isIndeterminate)
        iconColor = checkedColor;
    if (isHovered)
        iconColor = hoverColor;
    if (isDisabled)
        iconColor = disabledColor;
    return (React.createElement("label", { style: {
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
        }, ref: refLabel, "aria-disabled": isDisabled, onMouseUp: () => setIsPressed(false), onMouseDown: () => setIsPressed(true) },
        React.createElement(View, { sx: {
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                width: isSizeBig ? '$7' : '$6',
                height: isSizeBig ? '$7' : '$6',
                outlineWidth: 0,
                outlineStyle: 'solid',
                outlineOffset: 0,
                outlineColor: 'text',
                transition: 'outline-offset 0.15s ease',
                ...(shouldRenderFocus && {
                    outlineWidth: 1,
                    outlineOffset: 3,
                    ...$$focus,
                }),
                ...(shouldRenderChecked && $$checked),
                ...(shouldRenderHover && $$hover),
                ...(isDisabled && $$disabled),
            }, themeKey: "forms", variant: 'checkbox', testID: TEST_ID, accessibilityHint: accessibilityHint },
            React.createElement("input", { style: {
                    clip: 'rect(0 0 0 0)',
                    height: 1,
                    margin: -1,
                    overflow: 'hidden',
                    padding: 0,
                    position: 'absolute',
                    width: 1,
                    whiteSpace: 'nowrap',
                    wordWrap: 'normal',
                }, ref: ref, type: "checkbox", id: id, name: name, checked: isChecked, disabled: isDisabled, "aria-checked": isChecked, "aria-disabled": isDisabled, "aria-label": accessibilityLabel, onChange: () => onChange(isIndeterminate ? true : !isChecked) }),
            shouldRenderIcon && (React.createElement(View, { sx: { width: isSizeBig ? '$4' : '$3', position: 'absolute' } },
                shouldRenderIconCheck && React.createElement(IconCheck, { color: iconColor }),
                shouldRenderIconMinus && React.createElement(IconMinus, { color: iconColor })))),
        children && (React.createElement(Text, { sx: {
                fontSize: '$2',
                paddingLeft: '$3',
                lineHeight: '$4',
                fontWeight: '500',
                alignSelf: 'center',
            } }, children))));
});
const CheckboxCompound = Object.assign({}, Checkbox, { displayName: 'Checkbox', testID: TEST_ID });
export { CheckboxCompound as Checkbox };
//# sourceMappingURL=Checkbox.js.map